// =============================================================================
// Assistant menu guide — where things live in the dashboard and how to use them.
//
// Shared by the server (injected into the assistant prompt, filtered by role)
// and the admin client (shortcut buttons). The model may only return a
// `routeId`; the path always comes from this table, never from model output.
// Keep labels in sync with the sidebar (App.tsx SideNav) and page buttons.
// =============================================================================

export const ASSISTANT_ROUTE_IDS = ['playlists', 'schedule', 'scheduleReview', 'org', 'history'] as const;
export type AssistantRouteId = (typeof ASSISTANT_ROUTE_IDS)[number];

export type AssistantMenuPermission = 'content:read' | 'content:write' | 'content:approve' | 'history:read';

export type AssistantMenuEntry = {
  routeId: AssistantRouteId;
  /** Where the operator finds it, e.g. "스케줄 > 승인 관리". Also the shortcut label. */
  location: string;
  /** Hash-router path the shortcut opens. */
  path: string;
  /** Permission needed for the main actions (display hint only — routes enforce). */
  permission: AssistantMenuPermission;
  summary: string;
  steps: readonly string[];
};

export const ASSISTANT_MENU_GUIDE: readonly AssistantMenuEntry[] = [
  {
    routeId: 'playlists',
    location: '플레이리스트',
    path: '/media',
    permission: 'content:write',
    summary: '미디어 업로드, 플레이리스트 만들기·항목 구성·순서 변경, 슬라이드 디자인',
    steps: [
      '왼쪽 메뉴에서 "플레이리스트"를 연다',
      '"＋ 미디어 업로드하기"로 영상·이미지를 올린다',
      '"＋ 플레이리스트 만들기"로 새 플레이리스트를 만든다',
      '라이브러리에서 미디어·슬라이드를 골라 "플레이리스트에 추가"로 넣는다',
      '손잡이를 끌어 순서를 바꾸고 저장한다',
    ],
  },
  {
    routeId: 'schedule',
    location: '스케줄',
    path: '/schedule',
    permission: 'content:write',
    summary: '일·주·월 캘린더로 편성 보기, 스케줄 추가·변경·삭제, 대상 기기 선택',
    steps: [
      '왼쪽 메뉴에서 "스케줄"을 연다',
      '위쪽 "일 / 주 / 월"로 보기를 바꾼다',
      '"＋ 스케줄 추가"를 눌러 플레이리스트·시간·반복·대상 기기를 고른다',
      '이미 있는 스케줄은 캘린더에서 눌러 변경하거나 삭제한다',
    ],
  },
  {
    routeId: 'scheduleReview',
    location: '스케줄 > 승인 관리',
    path: '/schedule?section=review',
    permission: 'content:read',
    summary: '승인 요청 확인, 승인·반려(승인 권한이 있을 때)',
    steps: [
      '"스케줄" 화면 오른쪽 위 "승인 관리"를 누른다',
      '대기 중인 요청을 골라 내용을 확인한다',
      '승인 권한이 있으면 "승인" 또는 "반려"(사유 입력)를 누른다',
    ],
  },
  {
    routeId: 'org',
    location: '구성 관리',
    path: '/org',
    permission: 'content:read',
    summary: '그룹 만들기·수정(승인 방식 포함), 디바이스 추가·상세 보기, 관리자 추가',
    steps: [
      '왼쪽 메뉴에서 "구성 관리"를 연다',
      '"+ 그룹 만들기"로 그룹을 만들고, "그룹 수정"으로 이름·승인 방식을 바꾼다(승인 방식은 조직 Owner만)',
      '"+ 디바이스 추가"로 기기를 등록한다',
      '기기 행을 누르면 상세 정보를 볼 수 있다',
      '관리자는 "+ 관리자 추가"로 배정한다(권한이 있을 때)',
    ],
  },
  {
    routeId: 'history',
    location: '관리 이력',
    path: '/history',
    permission: 'history:read',
    summary: '승인·그룹 운영·콘텐츠 공유 이력 조회, 엑셀 다운로드',
    steps: [
      '왼쪽 메뉴에서 "관리 이력"을 연다(조회 권한이 있을 때만 보인다)',
      '유형과 시작일·종료일로 거른다',
      '"엑셀 다운로드"로 내려받는다',
    ],
  },
];

export function findAssistantMenu(routeId: string): AssistantMenuEntry | undefined {
  return ASSISTANT_MENU_GUIDE.find((entry) => entry.routeId === routeId);
}

// =============================================================================
// What the assistant itself can do — the admin panel's "할 수 있는 일" list;
// clicking an example fills the input. `tools` maps a group onto the server
// tool catalog: a main test fails when a catalog tool is neither listed here
// nor deliberately unlisted, or when `groupMode` disagrees with the scoped catalog.
// `permission` gates a group by role the way the planner does (display only —
// the planner and routes enforce); the same test pins the role table to rbac.
// =============================================================================

export type AssistantCapabilityPermission = 'content:write' | 'content:approve';

/** Roles holding each permission the assistant's starters gate on (mirrors rbac). */
export const ASSISTANT_PERMISSION_ROLES: Record<AssistantCapabilityPermission, readonly string[]> = {
  'content:write': ['Owner', 'Admin', 'Editor'],
  'content:approve': ['Owner', 'Admin'],
};

/** Display-only role check. No permission needed = any role; unknown role = no gated items. */
export function assistantRoleCan(role: string | null | undefined, permission?: AssistantCapabilityPermission): boolean {
  return !permission || ASSISTANT_PERMISSION_ROLES[permission].includes(role ?? '');
}

export type AssistantCapability = {
  title: string;
  summary: string;
  examples: readonly string[];
  /** Server tool names behind a change group. Omitted = answered from context, nothing changes. */
  tools?: readonly string[];
  /** Needed to use the group; omitted = any role. */
  permission?: AssistantCapabilityPermission;
  /** false: hidden where group permissions are enforced (the scoped catalog lacks these tools). */
  groupMode?: false;
};

export const ASSISTANT_CAPABILITIES: readonly AssistantCapability[] = [
  {
    title: '스케줄 편성',
    summary: '플레이리스트를 언제(기간·요일·매월·매년·음력 반복, 시간대), 어느 기기에 틀지 등록하고 고쳐요.',
    tools: ['assignments.create', 'assignments.update'],
    permission: 'content:write',
    examples: [
      '평일 12시부터 1시까지 로비에 점심 플레이리스트 틀어줘',
      '다음주 금요일 3시부터 4시까지 모든 기기에 신제품 영상 틀어줘',
      '매년 음력 1월 1일 하루 종일 설날 플레이리스트 틀어줘',
      '점심 편성을 1시부터 2시로 바꿔줘',
    ],
  },
  {
    title: '승인·반려',
    summary: '승인 대기 중인 요청을 승인하거나 사유를 붙여 반려해요.',
    tools: ['schedules.publish', 'schedules.reject'],
    permission: 'content:approve',
    examples: ['승인 대기 중인 스케줄 모두 승인해줘', '승인 대기 중인 스케줄 반려해줘, 사유는 시간 재확인'],
  },
  {
    title: '이름·표시 시간 변경',
    summary: '플레이리스트 이름, 공지 슬라이드 이름·표시 시간을 바꿔요.',
    tools: ['playlists.update', 'notices.update'],
    permission: 'content:write',
    groupMode: false,
    examples: ['점심 플레이리스트 이름을 "런치 루프"로 바꿔줘', '"휴관 안내" 공지 표시 시간을 15초로 바꿔줘'],
  },
  {
    title: '조회',
    summary: '현재 편성, 기기 상태(기기가 마지막으로 보낸 보고 기준), 승인 대기를 바로 알려드려요.',
    examples: ['지금 스케줄 확인해줘', '꺼져 있거나 장애 난 기기 있어?', '승인 대기 중인 스케줄 있어?'],
  },
  {
    title: '메뉴 안내',
    summary: '메뉴 위치와 사용 순서를 알려드리고 바로가기 버튼을 띄워요.',
    examples: ['플레이리스트는 어떻게 만들어?', '디바이스는 어떻게 추가해?', '관리 이력은 어디서 봐?'],
  },
];
